import { Box, Divider, Typography } from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import { FC, useMemo, useState } from "react";
import { CoachesFeedback, LearnersFeedback } from "./components";
import { UserEngagementAnalytics } from "./components/UserEngagementAnalytics";
import { useAppSelector } from "hooks/useAppSelector";
import { userSelector } from "reduxStore/auth/selectors";
import { organizationSelector } from "reduxStore/organization/selectors";
import { ROLES } from "types/user";
import {
  useGetOrganizationByUserIdQuery,
  useGetOrganizationsQuery,
} from "graphql/__generated__/hooks";
import { getNonTestOrganizationIds } from "utils/testOrganization";
import {
  DEFAULT_METRICS_DATE_RANGE,
  getMetricsSinceDate,
  MetricsDateRange,
} from "pages/Metrics/metricsDateRange";

export const Analytics: FC = () => {
  const user = useAppSelector(userSelector);
  const organizationsFromStore = useAppSelector(organizationSelector);
  const [dateRange, setDateRange] = useState<MetricsDateRange>(
    DEFAULT_METRICS_DATE_RANGE,
  );

  const isAdmin = user?.role === ROLES.ADMIN;

  const { data: userOrganizationsData } = useGetOrganizationByUserIdQuery({
    variables: { userId: user?.user_id || "" },
    skip: !user?.user_id || isAdmin,
  });

  const { data: allOrganizationsData } = useGetOrganizationsQuery({
    skip: !isAdmin,
  });

  const accessibleOrgIds = useMemo(() => {
    if (isAdmin) {
      const organizations =
        allOrganizationsData?.organization || organizationsFromStore || [];
      return getNonTestOrganizationIds(organizations);
    }
    return getNonTestOrganizationIds(userOrganizationsData?.organization || []);
  }, [
    allOrganizationsData,
    isAdmin,
    organizationsFromStore,
    userOrganizationsData,
  ]);

  const sinceDate = useMemo(() => getMetricsSinceDate(dateRange), [dateRange]);

  return (
    <MainLayoutContainer title="Analytics">
      <Box>
        <UserEngagementAnalytics
          orgIds={accessibleOrgIds}
          sinceDate={sinceDate}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
        <Divider sx={{ margin: "2rem 0" }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          Feedback response times
        </Typography>
        <CoachesFeedback />
        <Divider sx={{ margin: "2rem 0" }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          Learner progress
        </Typography>
        <LearnersFeedback />
      </Box>
    </MainLayoutContainer>
  );
};
